import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Ticket,
  Users,
  UsersRound,
  BarChart3,
  Settings,
  Tag,
  Mail,
  Puzzle,
  BookOpen,
  Clock,
  Zap,
  FileText,
  FormInput,
  Shield,
  AlertTriangle,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { canSeeSettings } from './settings-nav';

// TICKET-AGENT-04: every /settings/* route is gated by its own permission key
// at the route level (see App.tsx) — each Settings child below carries the
// exact `permission` its route requires, so the sidebar never shows a link
// a role can't actually open. `settings_admin` alone only unlocks the parent
// "Settings" nav item and the /settings index page; it does not imply access
// to any child page. All per-page keys default to super_admin + administrator
// only — every other role starts with none of these until a super_admin
// explicitly grants them via the role editor.

const navItems = [
  { label: 'nav.dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'nav.tickets', href: '/tickets', icon: Ticket },
  { label: 'nav.contacts', href: '/contacts', icon: Users },
  { label: 'nav.companies', href: '/companies', icon: UsersRound },
  { label: 'nav.reports', href: '/reports', icon: BarChart3 },
  {
    label: 'nav.settings',
    href: '/settings',
    icon: Settings,
    adminOnly: true,
    children: [
      { label: 'nav.agents', href: '/settings/agents', icon: Users, permission: 'manage_agents' },
      { label: 'nav.groups', href: '/settings/groups', icon: UsersRound, permission: 'manage_groups' },
      { label: 'nav.roles', href: '/settings/roles', icon: Shield, permission: 'manage_roles' },
      { label: 'nav.tags', href: '/settings/tags', icon: Tag, permission: 'manage_tags' },
      { label: 'nav.ticketFields', href: '/settings/ticket-fields', icon: FileText, permission: 'field_admin' },
      { label: 'nav.ticketForms', href: '/settings/ticket-forms', icon: FormInput, permission: 'form_admin' },
      { label: 'nav.businessHours', href: '/settings/business-hours', icon: Clock, permission: 'sla_admin' },
      { label: 'nav.slaPolicies', href: '/settings/sla-policies', icon: AlertTriangle, permission: 'sla_admin' },
      { label: 'nav.automations', href: '/settings/automations', icon: Zap, permission: 'manage_automations' },
      { label: 'nav.email', href: '/settings/email', icon: Mail, permission: 'manage_email_config' },
      { label: 'nav.jira', href: '/settings/jira', icon: Puzzle, permission: 'manage_jira' },
      { label: 'nav.notificationTemplates', href: '/settings/notification-templates', icon: Mail, permission: 'manage_notification_templates' },
      { label: 'nav.apiKeys', href: '/settings/api-keys', icon: Shield, permission: 'manage_api_keys' },
      { label: 'nav.cannedResponses', href: '/settings/canned-responses', icon: MessageSquare, permission: 'manage_canned_responses' },
      { label: 'nav.knowledgeBase', href: '/settings/kb', icon: BookOpen, permission: 'manage_kb' },
    ],
  },
] as const;

export function AdminSidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { can } = useAuth();
  const { t } = useI18n();
  const [collapsed, setCollapsed] = React.useState(() => window.localStorage.getItem('suzukidesk.sidebarCollapsed') === 'true');
  // The Settings parent nav item is visible whenever at least one child page
  // is reachable — a role granted only, say, `manage_canned_responses`
  // should still see "Settings" in the sidebar, even without `settings_admin`.
  const visibleItems = navItems.filter((item) => {
    if (!('adminOnly' in item && item.adminOnly)) return true;
    return canSeeSettings(can);
  });
  React.useEffect(() => {
    window.localStorage.setItem('suzukidesk.sidebarCollapsed', String(collapsed));
  }, [collapsed]);

  return (
    <aside className={cn('flex shrink-0 flex-col overflow-hidden border-r border-brand-800 bg-brand-900 text-white transition-[width]', collapsed ? 'w-16' : 'w-60')}>
      <div className={cn('flex border-b border-brand-800 p-3', collapsed ? 'justify-center' : 'justify-end')}>
        <button type="button" onClick={() => setCollapsed((value) => !value)} className="rounded p-1.5 text-brand-100 hover:bg-surface/10 hover:text-white" aria-label={collapsed ? t('shell.expandNavigation') : t('shell.collapseNavigation')} title={collapsed ? t('shell.expandNavigation') : t('shell.collapseNavigation')}>
          {collapsed ? <PanelLeftOpen className="h-4 w-4" aria-hidden="true" /> : <PanelLeftClose className="h-4 w-4" aria-hidden="true" />}
        </button>
      </div>
      <nav className={cn('flex-1 space-y-1 overflow-y-auto p-3', collapsed && 'px-2')} aria-label={t('shell.adminNavigation')}>
        {visibleItems.map((item) => {
          const isActive = pathname.startsWith(item.href);
          const Icon = item.icon;
          return (
            <div key={item.href}>
              <NavLink
                to={item.href}
                className={cn(
                  'flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  collapsed ? 'justify-center' : 'gap-3',
                  isActive ? 'bg-surface/15 text-white shadow-sm' : 'text-brand-100 hover:bg-surface/10 hover:text-white',
                )}
                title={collapsed ? t(item.label) : undefined}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {!collapsed && t(item.label)}
              </NavLink>
              {'children' in item && item.children && isActive && !collapsed && (
                <div className="ml-7 mt-1 space-y-1">
                  {item.children.filter((child) => !('permission' in child) || can('settings_admin') || can(child.permission)).map((child) => {
                    const ChildIcon = child.icon;
                    return (
                      <NavLink
                        key={child.href}
                        to={child.href}
                        className={cn(
                          'flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium',
                          pathname === child.href
                            ? 'bg-surface/10 text-white'
                            : 'text-brand-200 hover:bg-surface/10 hover:text-white',
                        )}
                      >
                        <ChildIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        {t(child.label)}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
